import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { freshnessBadgeClasses } from "../lib/metricEnvelope";

const API = process.env.REACT_APP_BACKEND_URL;

function readCustomerId() {
  try {
    const raw = sessionStorage.getItem("current_customer");
    if (!raw) return "";
    const parsed = JSON.parse(raw);
    return parsed?.id || "";
  } catch {
    return "";
  }
}

const STATEMENT_LABEL = {
  pl: "Profit & Loss",
  bs: "Balance Sheet",
  tb: "Trial Balance",
};

const SOURCE_LABEL = {
  tally_agent:    "Tally Agent",
  financial_sync: "Financial Sync",
  manual:         "Manual entry",
};

const VALIDATION_LABEL = {
  passed:  { label: "Validated", classes: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  pending: { label: "Pending",   classes: "bg-amber-50 text-amber-700 border-amber-200" },
  failed:  { label: "Rejected",  classes: "bg-rose-50 text-rose-700 border-rose-200" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "numeric", timeZone: "UTC",
    });
  } catch { return "—"; }
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
}

/**
 * Statement History — every versioned upload, immutable audit trail.
 *
 * Reads /api/business-intelligence/history. Renders a chronological
 * timeline of each upload with its statement type, period, version,
 * source, and validation status. The latest version per (type, period)
 * carries the "Latest" pill so users can identify which row is feeding
 * their current Boardroom.
 *
 * Designed as the permanent financial memory layer — no soft-delete
 * here, no edits. To replace a figure the user re-uploads, which
 * creates a NEW version row, never overwriting the previous one.
 */
export default function StatementHistoryPage() {
  const customerId = readCustomerId();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!customerId) { setLoading(false); setError("Please sign in to view history."); return; }
    (async () => {
      try {
        const r = await fetch(
          `${API}/api/business-intelligence/history`,
          { credentials: "include" },
        );
        const body = await r.json();
        if (cancelled) return;
        if (!r.ok) setError(body?.detail || `HTTP ${r.status}`);
        else setData(body);
      } catch (e) {
        if (!cancelled) setError(String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [customerId]);

  const grouped = useMemo(() => {
    const rows = data?.history || [];
    const buckets = {};
    rows.forEach((r) => {
      const key = `${r.statement_type}|${r.period_start || ""}|${r.period_end || ""}`;
      (buckets[key] = buckets[key] || []).push(r);
    });
    // Sort each bucket: latest version first (already from DB but ensure)
    Object.keys(buckets).forEach((k) => {
      buckets[k].sort((a, b) => (b.statement_version || 0) - (a.statement_version || 0));
    });
    // Build sorted group list: most-recent period_end first
    return Object.entries(buckets)
      .sort(([, a], [, b]) => {
        const aEnd = a[0]?.period_end || "";
        const bEnd = b[0]?.period_end || "";
        return bEnd.localeCompare(aEnd);
      });
  }, [data]);

  if (loading) {
    return <div className="px-6 py-10 text-[13px] text-slate-500"
                data-testid="history-loading">Loading history…</div>;
  }
  if (error) {
    return <div className="px-6 py-10 text-[13px] text-rose-600"
                data-testid="history-error">{error}</div>;
  }

  const total = data?.count || 0;

  return (
    <div className="px-4 tablet:px-5 desktop:px-6 py-6 desktop:py-8 max-w-5xl mx-auto" data-testid="history-page">
      <h1 className="text-3xl font-semibold text-slate-900 mb-1">
        Statement History
      </h1>
      <p className="text-[14px] text-slate-600 mb-6 max-w-2xl">
        Every upload is preserved as a versioned snapshot — your company&apos;s
        permanent financial memory. Re-uploading the same period creates a
        new version; older versions stay searchable here for audit.
      </p>

      {total === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white p-8 text-center"
             data-testid="history-empty">
          <p className="text-[14px] text-slate-600 mb-4">
            No uploads yet. Start your permanent financial history with your first statement.
          </p>
          <Link to="/financial-sync"
                data-testid="history-go-upload"
                className="inline-flex items-center rounded-lg bg-emerald-600 px-4 py-2 text-[13px] font-medium text-white hover:bg-emerald-700">
            Upload your first statement →
          </Link>
        </div>
      ) : (
        <>
          <div className="mb-4 text-[12px] text-slate-500"
               data-testid="history-count">
            {total} version{total === 1 ? "" : "s"} across {grouped.length} period{grouped.length === 1 ? "" : "s"}.
          </div>

          <div className="space-y-5">
            {grouped.map(([key, rows]) => {
              const head = rows[0];
              return (
                <div key={key}
                     data-testid={`history-group-${head.statement_type}-${head.period_end || "na"}`}
                     className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                  <div className="flex items-center justify-between bg-slate-50 px-4 py-3 border-b border-slate-200">
                    <div>
                      <div className="text-[13.5px] font-semibold text-slate-900">
                        {STATEMENT_LABEL[head.statement_type] || head.statement_type}
                        <span className="ml-2 text-[11.5px] font-normal text-slate-500">
                          · {fmtDate(head.period_start)} → {fmtDate(head.period_end)}
                        </span>
                      </div>
                      <div className="text-[11.5px] text-slate-500">
                        {head.period_fy || ""}{head.period_frequency ? ` · ${head.period_frequency}` : ""}
                      </div>
                    </div>
                    <div className="text-[11.5px] font-semibold text-slate-500">
                      {rows.length} version{rows.length === 1 ? "" : "s"}
                    </div>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {rows.map((r) => {
                      const status = VALIDATION_LABEL[r.validation_status] ||
                                     { label: r.validation_status || "Unknown",
                                       classes: "bg-slate-100 text-slate-600 border-slate-200" };
                      return (
                        <div key={r.id}
                             data-testid={`history-row-${r.id}`}
                             className="px-4 py-3 flex items-center justify-between text-[12.5px]">
                          <div className="flex items-center gap-3">
                            <span className="inline-flex items-center justify-center rounded-md bg-slate-100 px-2 py-1 text-[11px] font-semibold text-slate-700">
                              v{r.statement_version}
                            </span>
                            {r.is_latest_version && (
                              <span data-testid={`history-latest-${r.id}`}
                                    className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[10.5px] font-semibold text-emerald-700 border border-emerald-200">
                                Latest
                              </span>
                            )}
                            <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10.5px] ${status.classes}`}>
                              {status.label}
                            </span>
                            <span className="text-slate-700">
                              {SOURCE_LABEL[r.data_source] || r.data_source || "—"}
                            </span>
                          </div>
                          <div className="text-slate-500">
                            {fmtDateTime(r.synced_at)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
